import { useMemo } from "react";
import { useAppSelector } from "../redux/hooks";
import {
  adminRoutes,
  expeditionRoutes,
  fleetRoutes,
  reportsRoutes,
  transportRoutes,
} from "./routesConstants";

import userRoles from "../constants/userRoles";

interface DashboardCategories {
  color: string;
  title: string;
  subtitle?: string;
  subtitleValue?: string;
  categories: {
    id: string;
    title: string;
    path: string;
    subtitle?: string;
    value?: string | number;
    isIncreasing?: boolean;
  }[];
  navigationLinks?: {
    link: string;
    label: string;
  }[];
}

function useAppMenuRoutes() {
  const {
    user: { userRoutesAccess },
  } = useAppSelector((state) => ({
    user: state.user,
  }));

  const expeditionMenuRoutes = [
    {
      id: userRoles.AWB_HISTORY,
      title: "Expeditii active",
      path: `/${expeditionRoutes.activeExpeditions}`,
    },
    {
      id: userRoles.SCHEDULE_WORKING_POINTS,
      title: "Program locatii de livrare",
      path: `/${expeditionRoutes.workingPoints}`,
    },
    {
      id: userRoles.ROUTES_ORGANIZATION,
      title: "Organizare rute",
      path: `/${expeditionRoutes.routesOrganizationLocal}`,
    },
    {
      id: userRoles.NODES,
      title: "Organizare noduri",
      path: `/${expeditionRoutes.nodes}`,
    },
    {
      id: userRoles.CLIENTS,
      title: "Clienti",
      path: `/${expeditionRoutes.clients}`,
    },
    {
      id: userRoles.CREATE_BIN,
      title: "Formeaza BIN",
      path: `/${expeditionRoutes.createBin}`,
    },
    {
      id: userRoles.VIEW_CARGO_VOLUME,
      title: "Vizualizare volum marfa",
      path: `/${expeditionRoutes.viewCargoVolume}`,
    },
    {
      id: userRoles.BINS_VOLUME,
      title: "Volume marfa",
      path: `/${expeditionRoutes.binsVolume}`,
    },
  ].filter((route) => userRoutesAccess?.includes(route.id));

  const transportMenuRoutes = [
    {
      id: userRoles.ASSIGN_ROUTE,
      title: "Alocare ruta",
      path: `/${transportRoutes.assign}`,
    },
    {
      id: userRoles.ACTIVE_TRANSPORTS,
      title: "Transporturi active",
      path: `/${transportRoutes.activeTransports}`,
    },
  ].filter((route) => userRoutesAccess?.includes(route.id));

  const reportsMenuRoutes = [
    {
      id: userRoles.SEARCH_INVOICES_AWB,
      title: "Cauta expeditii dupa facturi",
      path: `/${reportsRoutes.searchInvoicesAwb}`,
    },
    {
      id: userRoles.CARGO_NODE,
      title: "Colete in nod",
      path: `/${reportsRoutes.cargoNode}`,
    },
    {
      id: userRoles.CARGO_CAR,
      title: "Colete in masina",
      path: `/${reportsRoutes.cargoCar}`,
    },
    {
      id: userRoles.VEHICLE_TEMPERATURE_CHART,
      title: "Diagrama temperatura autovehicul",
      path: `/${reportsRoutes.vehicleTemperatureChart}`,
    },
    {
      id: userRoles.UNDELIVERED_PACKAGES_CENTRALIZER,
      title: "Centralizator colete nelivrate",
      path: `/${reportsRoutes.undeliveredPackagesCentralizer}`,
    },
    {
      id: userRoles.PACKAGE_HISTORY,
      title: "Istoric colet",
      path: `/${reportsRoutes.packageHistory}`,
    },
    {
      id: userRoles.TEMPERATURE_ALERTS,
      title: "Alerte temperatura",
      path: `/${reportsRoutes.temperatureAlerts}`,
    },
  ].filter((route) => userRoutesAccess?.includes(route.id));

  const fleepMenuRoutes = [
    {
      id: userRoles.PREVIEW_FLEET,
      title: "Flota",
      path: `/${fleetRoutes.fleet}`,
    },
    {
      id: userRoles.CAR_DOCUMENTS_ALL_DISPLAY,
      title: "Documente masini",
      path: `/${fleetRoutes.carsDocuments}`,
    },
    {
      id: userRoles.ASSIGNED_CAR_NODE_DISPLAY,
      title: "Asignare masina la nod",
      path: `/${fleetRoutes.assignedCarNode}`,
    },
  ].filter((route) => userRoutesAccess?.includes(route.id));

  const adminMenuRoutes = [
    {
      id: userRoles.PREVIEW_USERS,
      title: "Utilizatori definiti in sistem",
      path: `/${adminRoutes.users}`,
    },
    {
      id: userRoles.CREATE_USER,
      title: "Creeaza utilizator",
      path: `/${adminRoutes.users}/${adminRoutes.createUser}`,
    },
    {
      id: userRoles.ADMINISTRATE_REASON,
      title: "Nomenclator motive",
      path: `/${adminRoutes.administrateReason}`,
    },
    {
      id: userRoles.SERVICES,
      title: "Administrare servicii prestate",
      path: `/${adminRoutes.services}`,
    },
    {
      id: userRoles.GENERATE_BARCODE,
      title: "Genereaza coduri de bare",
      path: `/${adminRoutes.generateBarcode}`,
    },
  ].filter((route) => userRoutesAccess?.includes(route.id));

  const categories: DashboardCategories[] = useMemo(
    () =>
      [
        {
          color: "info",
          title: "Flota",
          categories: fleepMenuRoutes,
        },
        {
          color: "info",
          title: "Expeditie",
          categories: expeditionMenuRoutes,
        },
        {
          color: "info",
          title: "Transport",
          categories: transportMenuRoutes,
        },
        {
          color: "info",
          title: "Rapoarte",
          categories: reportsMenuRoutes,
        },
        {
          color: "primary",
          title: "Administrare",
          categories: adminMenuRoutes,
        },
      ].filter((category) => category.categories.length > 0),
    [
      adminMenuRoutes,
      fleepMenuRoutes,
      expeditionMenuRoutes,
      transportMenuRoutes,
      reportsMenuRoutes,
    ],
  );

  return categories;
}

export default useAppMenuRoutes;
